import { useState, useEffect, useCallback, useRef } from "react";
import {
  initializeSocket,
  disconnectSocket,
  isSocketConnected,
  getSocket,
  joinManifestChat,
  sendMessage,
  listMessages,
  leaveManifestChat,
  onNewMessage,
  onMessages,
  onMessagesRead,
  off,
  autoJoinManifestChats,
} from "src/services/socketChatService";

/**
 * React hook for managing chat functionality
 * Provides methods for joining chats, sending/receiving messages, and managing read receipts
 */
export const useChat = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [messages, setMessages] = useState({});
  const [unreadCounts, setUnreadCounts] = useState({});
  const [error, setError] = useState(null);
  const callbacksRef = useRef({});

  // Initialize socket connection
  useEffect(() => {
    const socket = getSocket();
    if (socket) {
      setIsConnected(isSocketConnected());

      // Listen for connection status changes
      socket.on("connect", () => {
        setIsConnected(true);
        console.log("Chat socket connected");
      });

      socket.on("disconnect", () => {
        setIsConnected(false);
        console.log("Chat socket disconnected");
      });

      socket.on("connect_error", (err) => {
        console.error("Chat socket connection error:", err);
        setError(err.message);
        setIsConnected(false);
      });
    } else {
      // Socket not initialized yet, wait for initialization
      console.log("Socket not initialized yet, waiting...");
    }

    return () => {
      // Cleanup on unmount
      const currentSocket = getSocket();
      if (currentSocket) {
        Object.values(callbacksRef.current).forEach((callback) => {
          off("new_message", callback);
          off("messages", callback);
          off("messages_read", callback);
        });
        callbacksRef.current = {};
      }
    };
  }, []);

  // Join a manifest chat
  const joinChat = useCallback(async (manifestUuid) => {
    try {
      const data = await joinManifestChat(manifestUuid);
      setUnreadCounts((prev) => ({
        ...prev,
        [manifestUuid]: {
          admin: data.unreadAdminCount || 0,
          driver: data.unreadDriverCount || 0,
        },
      }));
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // Send a message
  const send = useCallback(async (manifestUuid, text) => {
    try {
      const data = await sendMessage(manifestUuid, text);
      setUnreadCounts((prev) => ({
        ...prev,
        [manifestUuid]: {
          admin: data.unreadAdminCount || 0,
          driver: data.unreadDriverCount || 0,
        },
      }));
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // List messages for a chat
  const fetchMessages = useCallback(async (manifestUuid, limit = 50) => {
    try {
      const data = await listMessages(manifestUuid, limit);
      setMessages((prev) => ({
        ...prev,
        [manifestUuid]: data.messages || [],
      }));
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // Leave a chat
  const leaveChat = useCallback(async (manifestUuid) => {
    try {
      await leaveManifestChat(manifestUuid);
      setMessages((prev) => {
        const newMessages = { ...prev };
        delete newMessages[manifestUuid];
        return newMessages;
      });
      setUnreadCounts((prev) => {
        const newCounts = { ...prev };
        delete newCounts[manifestUuid];
        return newCounts;
      });
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // Auto-join multiple manifest chats
  const autoJoinChats = useCallback(async (manifestUuids) => {
    try {
      await autoJoinManifestChats(manifestUuids);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // Listen for new messages
  const listenNewMessage = useCallback((callback) => {
    const handler = (data) => {
      const { chatUuid, message, unreadAdminCount, unreadDriverCount } = data;

      // Update messages
      setMessages((prev) => {
        // Find manifestUuid by chatUuid (you may need to track this mapping)
        const updated = { ...prev };
        Object.keys(updated).forEach((manifestUuid) => {
          if (!updated[manifestUuid]) updated[manifestUuid] = [];
          updated[manifestUuid].push(message);
        });
        return updated;
      });

      // Update unread counts
      setUnreadCounts((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((manifestUuid) => {
          updated[manifestUuid] = {
            admin: unreadAdminCount || 0,
            driver: unreadDriverCount || 0,
          };
        });
        return updated;
      });

      callback(data);
    };

    callbacksRef.current.newMessage = handler;
    onNewMessage(handler);

    // Return cleanup function
    return () => {
      off("new_message", handler);
      delete callbacksRef.current.newMessage;
    };
  }, []);

  // Listen for messages list
  const listenMessages = useCallback((callback) => {
    const handler = (data) => {
      const { manifestUuid, messages: msgList } = data;

      setMessages((prev) => ({
        ...prev,
        [manifestUuid]: msgList || [],
      }));

      callback(data);
    };

    callbacksRef.current.messages = handler;
    onMessages(handler);

    // Return cleanup function
    return () => {
      off("messages", handler);
      delete callbacksRef.current.messages;
    };
  }, []);

  // Listen for read receipts
  const listenMessagesRead = useCallback((callback) => {
    const handler = (data) => {
      const { manifestUuid, messages: msgList } = data;

      setMessages((prev) => {
        const updated = { ...prev };
        if (updated[manifestUuid] && msgList) {
          updated[manifestUuid] = msgList;
        }
        return updated;
      });

      callback(data);
    };

    callbacksRef.current.messagesRead = handler;
    onMessagesRead(handler);

    // Return cleanup function
    return () => {
      off("messages_read", handler);
      delete callbacksRef.current.messagesRead;
    };
  }, []);

  // Get messages for a specific manifest
  const getMessages = useCallback(
    (manifestUuid) => {
      return messages[manifestUuid] || [];
    },
    [messages],
  );

  // Get unread count for a specific manifest
  const getUnreadCount = useCallback(
    (manifestUuid) => {
      return unreadCounts[manifestUuid] || { admin: 0, driver: 0 };
    },
    [unreadCounts],
  );

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // Connection status
    isConnected,
    error,
    clearError,

    // Chat operations
    joinChat,
    send,
    fetchMessages,
    leaveChat,
    autoJoinChats,

    // Event listeners
    listenNewMessage,
    listenMessages,
    listenMessagesRead,

    // Data accessors
    getMessages,
    getUnreadCount,

    // Utility
    disconnect: disconnectSocket,
  };
};

export default useChat;
